import { Icon } from "@mdi/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { mdiChevronDown } from "@mdi/js";
import { Button, DatePicker, Select } from "antd";
import { widgetHoverAtom } from "atoms/interaction";
import classnames from "classnames";
import { RigFracTimeScale } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracActivity";
import { GRID_BOTTOM_ROTATED } from "entities/charts/eva/translators/rigFracActivity";
import { useAtomValue } from "jotai";
import moment from "moment";
import type { Moment } from "moment";
import type { RangeValue } from "rc-picker/lib/interface";
import { updateChart } from "store/features/charts/chartSlice";
import styled from "styled-components/macro";
import { ChartRequestType, RigFracActivityChartRequestType } from "types";

import { useChartEntities } from "hooks/charts";

import { visibilityToggleStyles } from "../../../shared/SharedStyles";

const { RangePicker } = DatePicker;

const TIME_SCALE_OPTIONS = [
  { label: "Weekly", value: RigFracTimeScale.Weekly as string },
  { label: "Monthly", value: RigFracTimeScale.Monthly as string },
  { label: "Quarterly", value: RigFracTimeScale.Quarterly as string },
  { label: "Custom", value: RigFracTimeScale.Custom as string }
];

const ISO_DAY = "YYYY-MM-DD";

/**
 * Placeholders for the two range inputs, in order. Doubles as the identity of
 * each end when reading typed text back out — see handlePickerInput.
 */
const RANGE_PLACEHOLDERS: [string, string] = ["Start date", "End date"];

export interface TimeScaleSelectorProps {
  /** Selectable scales, in menu order. Defaults to the rig/frac set. */
  options?: { label: string; value: string }[];
  /** The value meaning "Custom" — selecting it reveals the range picker. */
  customValue?: string;
  /**
   * Request defaults the dispatched selection is spread over, so a chart whose
   * request has other required fields still writes a complete one.
   */
  defaultRequest?: GenericObject;
  /** Prefix for this instance's test ids and dropdown class. */
  testIdPrefix?: string;
  /**
   * Grid bottom inset (px) to fall back on when the chart has not persisted
   * `grid` in its options. Only used for positioning.
   */
  fallbackGridBottom?: number;
}

// Valid iff both ends are set and from <= to — no upper bound on range width
// (ISO day strings compare correctly as strings).
export const isCustomRangeValid = (from: Maybe<string>, to: Maybe<string>): boolean =>
  !!from && !!to && from <= to;

/**
 * Time-scale control rendered on a chart's x-axis, overlaying the axis title on
 * hover. The fixed scales dispatch immediately; Custom requires a date range and
 * only dispatches on Apply.
 *
 * Defaults to the Rig/Frac Activity chart (EVA-76 US2) — Weekly | Monthly |
 * Quarterly | Custom. The Well Status Over Time chart passes its own
 * scale set and request defaults; everything else, especially the Custom
 * draft/Apply interaction and its several antd workarounds below, is shared
 * rather than reimplemented per chart.
 */
export function TimeScaleSelector({
  options: scaleOptions = TIME_SCALE_OPTIONS,
  customValue = RigFracTimeScale.Custom,
  defaultRequest = DEFAULT_RIG_FRAC_ACTIVITY_REQUEST,
  testIdPrefix = "rig-frac",
  fallbackGridBottom = GRID_BOTTOM_ROTATED
}: TimeScaleSelectorProps = {}) {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { request, screenshot, dataTable, options, chartType } = useChartEntities(id);
  const widgetHoverMap = useAtomValue(widgetHoverAtom);
  const isHover = widgetHoverMap?.[id] ?? false;

  // Overlay the "Time" axis title on hover (wells convention: the hover control
  // covers the title it edits). The title's centre sits (grid.bottom −
  // xAxis.nameGap) above the widget bottom. BOTH terms are size-aware — nameGap
  // varies with the bucket-label length, and grid.bottom varies with whether the
  // labels are rotated — so read them LIVE from the persisted options rather
  // than fixing a CSS offset. Charts that persist only their axes (not `grid`)
  // fall back to the caller's constant, which is what rig/frac has always used.
  // 12 ≈ half this selector's height.
  const bottomPx = useMemo(() => {
    try {
      const parsed = JSON.parse(options || "{}");
      // Distrust axes another chart type produced. This JSON survives a chart-type
      // switch, and the two Well Status charts have different grid insets and
      // different nameGaps — which is the whole reason both are read live — so
      // after a Current → Over Time switch this button positioned itself from the
      // Current chart's geometry until the first over-time response landed, then
      // jumped. Same bug WellStatusSelector guards, opposite direction.
      //
      // TOLERANT on purpose: only distrust a stamp that is PRESENT and disagrees.
      // This component is shared with rig/frac, whose options carry no stamp at
      // all, so a strict `!==` would send every rig/frac render to the fallback
      // permanently — trading one chart's transient glitch for another's constant
      // one.
      if (parsed?.chartType && parsed.chartType !== chartType) return 36;
      const nameGap = parsed?.xAxis?.nameGap;
      if (typeof nameGap !== "number") return 36;
      const gridBottom = parsed?.grid?.bottom;
      const bottomInset =
        typeof gridBottom === "number" ? gridBottom : fallbackGridBottom;
      return Math.max(2, bottomInset - nameGap - 12);
    } catch {
      return 36;
    }
  }, [options, fallbackGridBottom, chartType]);

  // Lean rig/frac request rides the entity's ChartRequestType-typed field —
  // same cast pattern as the controller hook.
  const selections = request as unknown as Maybe<
    Partial<RigFracActivityChartRequestType>
  >;
  const appliedScale =
    (selections?.timeScale as string) ?? (defaultRequest.timeScale as string);
  const appliedRange = selections?.customRange ?? null;

  // Custom is draft-first: picking it only reveals the range picker; the
  // request is untouched until a valid range is applied.
  const [isCustomDraft, setIsCustomDraft] = useState(false);
  // The RangePicker dropdown is controlled so it stays open through date
  // selection: Apply commits + closes, while an outside click / escape closes
  // and discards the draft. Picking dates must NOT close it (#4).
  const [pickerOpen, setPickerOpen] = useState(false);
  // A completed date pick makes antd auto-close the calendar; this flag tells
  // onOpenChange to keep it open (the user commits with Apply) instead of
  // treating that auto-close as a dismissal.
  const justPickedRef = useRef(false);
  // Clicking Apply while the calendar is open reads as an outside click, firing
  // onOpenChange(false) BEFORE the button's onClick. Without this flag that path
  // runs handleDismiss — which unmounts the Apply button and reverts the draft,
  // so the click is lost and Apply appears to do nothing. Set on Apply's
  // mousedown so onOpenChange just closes the dropdown and lets handleApply commit.
  const applyMouseDownRef = useRef(false);
  const [draftFrom, setDraftFrom] = useState<Nullable<string>>(
    appliedRange?.from ?? null
  );
  const [draftTo, setDraftTo] = useState<Nullable<string>>(appliedRange?.to ?? null);

  // Keep the draft in step when the applied range changes elsewhere (e.g. session restore).
  useEffect(() => {
    setDraftFrom(appliedRange?.from ?? null);
    setDraftTo(appliedRange?.to ?? null);
  }, [appliedRange?.from, appliedRange?.to]);

  const displayedScale = isCustomDraft ? customValue : appliedScale;
  const showRangePicker = displayedScale === customValue;
  const applyEnabled = isCustomRangeValid(draftFrom, draftTo);

  const dispatchRequest = (
    timeScale: string,
    customRange: Nullable<{ from: string; to: string }>
  ) => {
    dispatch(
      updateChart({
        id,
        request: {
          ...defaultRequest,
          ...selections,
          timeScale,
          customRange
        } as unknown as ChartRequestType
      })
    );
  };

  const handleScaleChange = (scale: string) => {
    if (scale === customValue) {
      setIsCustomDraft(true);
      setPickerOpen(true);
      return;
    }
    setIsCustomDraft(false);
    setPickerOpen(false);
    // Leaving Custom always clears the custom range.
    dispatchRequest(scale, null);
  };

  // The draft is what Apply reads, so it has to track BOTH ways a date arrives.
  const readDraft = (dates: RangeValue<Moment>) => {
    setDraftFrom(dates?.[0] ? dates[0].format(ISO_DAY) : null);
    setDraftTo(dates?.[1] ? dates[1].format(ISO_DAY) : null);
  };

  const handleRangeChange = (dates: RangeValue<Moment>) => {
    readDraft(dates);
    // A completed pick auto-closes the calendar — flag it so onOpenChange keeps
    // the dropdown open; the user commits with Apply.
    justPickedRef.current = true;
    setPickerOpen(true);
  };

  /**
   * Reports a CALENDAR-PANEL selection — a clicked date, or a typed one confirmed
   * with Enter — including the half-filled state after only one end is chosen,
   * which `onChange` (confirmed ranges only) never reports.
   *
   * It does NOT report typing. rc-picker's `onTextChange` updates only its own
   * internal selection and never calls `triggerChange`, so no callback fires per
   * keystroke; that path is handled by `handlePickerInput` below.
   *
   * Deliberately does NOT touch `justPickedRef` or the open state the way
   * `handleRangeChange` does: that flag exists to swallow the auto-close after a
   * completed pick, and setting it here would swallow the user's next
   * outside-click dismissal instead.
   */
  const handleCalendarChange = (dates: RangeValue<Moment>) => {
    readDraft(dates);
  };

  /**
   * Reads dates the user TYPES — which antd never reports.
   *
   * rc-picker's `onTextChange` (RangePicker.js) parses the text and calls only
   * `setSelectedValue` + `setViewDate`; it never calls `triggerChange`. So while
   * typing, NEITHER `onChange` nor `onCalendarChange` fires — the parent hears
   * nothing until the user presses Enter, which is not something anyone expects to
   * have to do. That is why Apply stayed grey with both boxes visibly filled in.
   * Listening to the inputs' own `input` events is what makes a typed range reach
   * the draft.
   *
   * Calendar clicks do NOT double-report through here: rc-picker sets the input
   * text from React state, which fires no `input` event.
   */
  const handlePickerInput = (event: React.FormEvent<HTMLSpanElement>) => {
    const target = event.target as HTMLElement;
    if (!(target instanceof HTMLInputElement)) return;

    // Identify the end by its PLACEHOLDER, which is ours (passed as a prop
    // below), falling back to sibling position only when that fails. Position
    // alone is coupled to rc-picker's internal DOM: if it ever renders an extra
    // input inside this wrapper, both ends would silently start writing to the
    // wrong draft field with nothing to signal it.
    let index: number = RANGE_PLACEHOLDERS.indexOf(target.placeholder);
    if (index === -1) {
      index = Array.from(event.currentTarget.querySelectorAll("input")).indexOf(target);
    }
    if (index !== 0 && index !== 1) return;
    const setDraft = index === 0 ? setDraftFrom : setDraftTo;

    const raw = target.value.trim();
    const current = index === 0 ? draftFrom : draftTo;

    // Emptied by hand — a definite intent to clear that end.
    if (raw === "") {
      setDraft(null);
      return;
    }

    const parsed = moment(raw, ISO_DAY, true);
    if (parsed.isValid()) {
      setDraft(parsed.format(ISO_DAY));
      return;
    }

    // Invalid text. Which way the user is going decides what to do, and the two
    // cases need opposite handling:
    //
    //  * GROWING toward a date ("2025-0" on the way to "2025-01-01") — hold the
    //    previous value. Clearing here would drive `rangeValue` back to null
    //    mid-word, and rc-picker echoes the controlled value into the input,
    //    wiping the text being typed.
    //  * SHRINKING an already-committed date (backspacing "2025-06-30" down to
    //    "2025-0") — clear it. Holding there left Apply enabled and able to commit
    //    a range the box was no longer showing.
    //
    // Length against the held value separates them: an edit that is shorter than
    // what is committed is a deletion in progress, not a completion.
    if (current && raw.length < current.length) {
      setDraft(null);
    }
  };

  const handleApply = () => {
    if (!applyEnabled || !draftFrom || !draftTo) return;
    setIsCustomDraft(false);
    setPickerOpen(false);
    dispatchRequest(customValue, { from: draftFrom, to: draftTo });
  };

  // Dismiss (outside click / escape): close the picker WITHOUT applying and
  // revert to the applied scale (drop the draft). If Custom was already the
  // applied scale, stay on it.
  const handleDismiss = () => {
    setIsCustomDraft(false);
    setPickerOpen(false);
    setDraftFrom(appliedRange?.from ?? null);
    setDraftTo(appliedRange?.to ?? null);
  };

  /**
   * The picker is CONTROLLED, so this is the whole of what it displays — and each
   * end has to be independent.
   *
   * Collapsing to `null` unless BOTH ends were set is what made typed dates
   * unusable: the moment a user finished typing the start date, this rendered
   * `null` back into a picker that had only one end, wiping the text they had
   * just entered. They never reached a second date, so `applyEnabled` never
   * turned true and Apply looked dead. Clicking dates through the calendar
   * happened to survive it only because rc-picker keeps its own in-progress
   * selection while the panel is open.
   *
   * `[Moment, null]` is a legal RangeValue — a half-filled range is exactly the
   * state a user is in mid-entry, and it must round-trip.
   */
  const rangeValue: RangeValue<Moment> =
    draftFrom || draftTo
      ? [draftFrom ? moment(draftFrom) : null, draftTo ? moment(draftTo) : null]
      : null;

  // While drafting a custom range the selector must NOT hide when the pointer
  // leaves the chart (e.g. moving into the calendar dropdown) — stay visible
  // until Apply/Cancel. Otherwise it's the normal hover-gated control.
  const visible = isCustomDraft || (isHover && !screenshot?.active && !dataTable?.active);

  return (
    <Wrapper
      $bottom={bottomPx}
      className={classnames({ visible })}
      data-testid={`${testIdPrefix}-time-scale`}>
      <Select<string>
        size="small"
        value={displayedScale}
        options={scaleOptions}
        onChange={handleScaleChange}
        dropdownMatchSelectWidth={false}
        // Styling for this dropdown lives in antd.css keyed on the rig/frac
        // class; every chart reuses it so the list looks identical.
        dropdownClassName="rig-frac-time-scale-dropdown"
        // Cancel rc-select's built-in [0, 4] open offset so the list sits flush
        // against the select box. `dropdownAlign` replaces the placement's
        // offset (rc-trigger merges `{...builtin, ...align}`), so [0, 0] is
        // flush — and it's placement-aware, staying flush when antd flips the
        // list to open upward (a plain negative margin would push it the wrong
        // way on that flip).
        dropdownAlign={{ offset: [0, 0] }}
        suffixIcon={<Icon path={mdiChevronDown} size={0.75} />}
      />
      {showRangePicker && (
        <>
          {/* Wraps ONLY the picker so `input` events can be attributed to its two
              date boxes by position — the scale Select renders an input of its own. */}
          <PickerInputs onInput={handlePickerInput}>
            <RangePicker
              size="small"
              value={rangeValue}
              open={pickerOpen}
              // Keep the dropdown open through date selection (justPicked guard);
              // a genuine dismissal (outside click / escape) closes it and drops
              // the draft. Apply commits + closes via its own handler (#4).
              onOpenChange={(nextOpen) => {
                if (nextOpen) {
                  setPickerOpen(true);
                  return;
                }
                if (justPickedRef.current) {
                  justPickedRef.current = false;
                  setPickerOpen(true);
                  return;
                }
                // Closing because the user is clicking Apply — just close the
                // dropdown and let handleApply commit; do NOT dismiss (which would
                // unmount the button and drop the draft before the click lands).
                if (applyMouseDownRef.current) {
                  applyMouseDownRef.current = false;
                  setPickerOpen(false);
                  return;
                }
                handleDismiss();
              }}
              onChange={handleRangeChange}
              onCalendarChange={handleCalendarChange}
              allowClear
              format={ISO_DAY}
              placeholder={RANGE_PLACEHOLDERS}
              data-testid={`${testIdPrefix}-custom-range`}
            />
          </PickerInputs>
          <Button
            size="small"
            type="primary"
            disabled={!applyEnabled}
            // Flag the mousedown so the RangePicker's resulting onOpenChange(false)
            // (this click reads as an outside click) commits via handleApply
            // instead of dismissing. mousedown fires before the picker closes.
            onMouseDown={() => {
              applyMouseDownRef.current = true;
            }}
            onClick={handleApply}
            data-testid={`${testIdPrefix}-custom-range-apply`}>
            Apply
          </Button>
        </>
      )}
    </Wrapper>
  );
}

export default TimeScaleSelector;

/* Transparent to layout — the picker stays a single flex item of Wrapper, exactly
   as it was before this span existed. Its only job is to be the event target that
   `input` bubbles to (see handlePickerInput). */
const PickerInputs = styled.span`
  display: inline-flex;
`;

const Wrapper = styled.div<{ $bottom: number }>`
  ${visibilityToggleStyles};
  position: absolute;
  /* Centred on the "Time" axis title (derived from the live xAxis nameGap —
     see bottomPx above) so the hover control covers the title it edits. */
  bottom: ${(p) => p.$bottom}px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 1;

  .ant-select-single.ant-select-sm:not(.ant-select-customize-input) .ant-select-selector {
    padding: 0 6px 0 12px;
  }

  /* Same chevron as the axis selector buttons. antd sizes the arrow box for a
     12px glyph, so recentre it for the 18px icon and reserve its width (+2px
     gap) beside the label. */
  .ant-select-arrow {
    right: 6px;
    width: auto;
    height: auto;
    margin-top: 0;
    transform: translateY(-50%);
    color: var(--color-text);
  }

  .ant-select-single.ant-select-show-arrow .ant-select-selection-item {
    padding-right: 20px;
  }
`;
